import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Swords, Crown, Skull, Trophy, Clock, Loader2, ChevronRight, Flame, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { SkeletonBossHero, SkeletonList } from '../components/Skeleton';

const TIER_META = {
  elite:     { color: '#8ECAE6', label: 'Elite',     glow: 'shadow-[0_0_28px_-8px_#8ECAE6]' },
  legendary: { color: '#E9C46A', label: 'Legendary', glow: 'shadow-[0_0_28px_-8px_#E9C46A]' },
  mythic:    { color: '#F43F5E', label: 'Mythic',    glow: 'shadow-[0_0_28px_-8px_#F43F5E]' },
};

export default function WorldBosses() {
  const nav = useNavigate();
  const [active, setActive] = useState([]);
  const [recent, setRecent] = useState([]);
  const [participation, setParticipation] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([
      api.get('/world-bosses').then(r => r.data).catch(() => ({ active: [], recent: [] })),
      api.get('/world-bosses/my/participation').then(r => r.data.participation).catch(() => []),
    ]).then(([b, p]) => {
      setActive(b.active || []);
      setRecent(b.recent || []);
      setParticipation(p || []);
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="pb-24 space-y-5 pt-2" data-testid="world-bosses-loading">
      <div className="space-y-1">
        <div className="h-2.5 w-24 bg-white/5 rounded animate-pulse" />
        <div className="h-6 w-40 bg-white/5 rounded animate-pulse" />
      </div>
      <div className="space-y-3">
        <SkeletonBossHero />
        <SkeletonBossHero />
      </div>
      <SkeletonList rows={3} avatar={false} />
    </div>
  );

  const unclaimed = participation.filter((p) =>
    !p.rewards_claimed &&
    (p.boss?.status === 'defeated' || p.boss?.status === 'expired')
  );

  return (
    <div className="pb-24 space-y-5" data-testid="world-bosses-page">
      <div className="pt-2">
        <p className="text-[10px] uppercase tracking-[0.25em] text-rose-400">Guild Events</p>
        <h1 className="font-serif text-3xl">World Bosses</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Cooperate with every Explorer online to fell legendary creatures. Damage share decides the loot tier.
        </p>
      </div>

      {unclaimed.length > 0 && (
        <section data-testid="unclaimed-boss-rewards" className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">Unclaimed rewards</p>
          {unclaimed.map((p) => (
            <UnclaimedRewardCard key={p.boss_id} entry={p} onClaim={load} />
          ))}
        </section>
      )}

      {active.length > 0 ? (
        <section data-testid="active-bosses">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Active raids</p>
          <div className="space-y-3">
            {active.map((b) => (
              <BossHero key={b.boss_id} boss={b} onOpen={() => nav(`/world-bosses/${b.boss_id}`)} />
            ))}
          </div>
        </section>
      ) : (
        <div className="rounded-2xl border border-white/5 bg-card p-6 text-center text-xs text-muted-foreground" data-testid="no-active-bosses">
          <ShieldAlert className="w-8 h-8 mx-auto mb-2 text-white/20" />
          No world boss is stirring right now. Check back soon — new raids spawn regularly.
        </div>
      )}

      {recent.filter((r) => r.status !== 'active').length > 0 && (
        <section data-testid="recent-bosses">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Recent raids</p>
          <div className="space-y-2">
            {recent.filter((r) => r.status !== 'active').slice(0, 5).map((b) => (
              <RecentBossRow key={b.boss_id} boss={b} onOpen={() => nav(`/world-bosses/${b.boss_id}`)} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function BossHero({ boss, onOpen }) {
  const tier = TIER_META[boss.tier] || TIER_META.legendary;
  const pct = Math.max(0, Math.min(100, (boss.hp_current / boss.hp_total) * 100));
  const daysLeft = (() => {
    try {
      const end = new Date(boss.expires_at);
      const diff = end.getTime() - Date.now();
      const h = Math.max(0, Math.round(diff / 3600000));
      return h < 24 ? `${h}h` : `${Math.round(h / 24)}d`;
    } catch { return null; }
  })();
  return (
    <motion.button
      onClick={onOpen}
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`boss-hero-${boss.boss_id}`}
      className={`w-full text-left rounded-3xl overflow-hidden border p-4 relative bg-gradient-to-br from-black via-black to-rose-950/40 ${tier.glow} border-white/10 hover:border-white/25 transition`}
    >
      <div className="flex items-start gap-3">
        {boss.image_url ? (
          <img src={boss.image_url} alt="" className="w-16 h-16 rounded-2xl object-cover border border-white/10" />
        ) : (
          <div className="w-16 h-16 rounded-2xl bg-black/50 border border-white/10 flex items-center justify-center">
            <Skull className="w-7 h-7" style={{ color: tier.color }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-widest font-mono" style={{ color: tier.color }}>
              {tier.label}
            </span>
            {daysLeft && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Clock className="w-3 h-3" /> {daysLeft}</span>}
          </div>
          <p className="font-serif text-lg leading-tight truncate">{boss.name}</p>
          <div className="mt-2">
            <div className="h-2 bg-black/50 rounded-full overflow-hidden">
              <motion.div initial={{ width: '100%' }} animate={{ width: `${pct}%` }}
                transition={{ duration: 0.7 }}
                className="h-full"
                style={{ background: `linear-gradient(90deg, ${tier.color}, #F43F5E)` }} />
            </div>
            <div className="flex items-center justify-between mt-1 text-[10px] font-mono text-white/70">
              <span>{boss.hp_current.toLocaleString()} / {boss.hp_total.toLocaleString()} HP</span>
              <span>{boss.total_participants} raiders</span>
            </div>
          </div>
        </div>
      </div>
    </motion.button>
  );
}

function RecentBossRow({ boss, onOpen }) {
  const tier = TIER_META[boss.tier] || TIER_META.legendary;
  const defeated = boss.status === 'defeated';
  return (
    <button
      onClick={onOpen}
      data-testid={`recent-boss-${boss.boss_id}`}
      className="w-full flex items-center gap-3 rounded-2xl bg-card border border-white/5 p-3 text-left hover:border-white/15"
    >
      <div className="w-9 h-9 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center">
        {defeated ? <Trophy className="w-4 h-4 text-amber-400" /> : <Clock className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">{boss.name}</p>
        <p className="text-[10px] uppercase tracking-widest" style={{ color: tier.color }}>
          {tier.label} · {boss.status}
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground" />
    </button>
  );
}

function UnclaimedRewardCard({ entry, onClaim }) {
  const [claiming, setClaiming] = useState(false);
  const handle = async () => {
    setClaiming(true);
    try {
      const res = await api.post(`/world-bosses/${entry.boss_id}/claim`);
      toast.success(`Rewarded ${res.data.coins || 0} coins as ${res.data.tier}.`);
      onClaim?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not claim');
    } finally {
      setClaiming(false);
    }
  };
  return (
    <button
      onClick={handle}
      disabled={claiming}
      data-testid={`claim-boss-${entry.boss_id}`}
      className="w-full rounded-2xl bg-gradient-to-r from-amber-500 to-yellow-400 text-black px-3 py-2.5 text-left flex items-center gap-3 hover:from-amber-400 hover:to-yellow-300 shadow-md disabled:opacity-50"
    >
      <Trophy className="w-5 h-5" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate">{entry.boss?.name}</p>
        <p className="text-[10px] uppercase tracking-widest">
          {entry.boss?.status === 'defeated' ? 'Victory' : 'Fell short'} · {(entry.damage_share * 100).toFixed(1)}% damage share
        </p>
      </div>
      <span className="font-mono text-xs">{claiming ? '...' : 'Claim'}</span>
    </button>
  );
}
